/**
 * Pipedrive Organizations Page
 * List of organizations with search, filtering, and pagination
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  usePipedriveOrganizations,
  usePipedriveOrganizationFilters,
} from '@/hooks/use-pipedrive-api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Building2, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import type { PipedriveOrganization } from '@/types/pipedrive'

export default function PipedriveOrganizations() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [owner, setOwner] = useState('')
  const [industry, setIndustry] = useState('')
  const [marketing, setMarketing] = useState('')
  const limit = 25

  const { data, isLoading, error } = usePipedriveOrganizations({
    page,
    limit,
    search,
    owner: owner || undefined,
    industry: industry || undefined,
    marketing: marketing || undefined,
  })

  const { data: filters } = usePipedriveOrganizationFilters()

  const organizations = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 1

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setOwner('')
    setIndustry('')
    setMarketing('')
    setPage(1)
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Organizations</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load organizations. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" aria-hidden="true" />
            Organizations
          </h1>
          <p className="text-muted-foreground">{total.toLocaleString()} organizations</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search organizations..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search organizations"
                />
              </div>
            </div>
            <Select
              value={owner || '__all__'}
              onValueChange={(v) => {
                setOwner(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]" aria-label="Filter by owner">
                <SelectValue placeholder="Owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Owners</SelectItem>
                {filters?.owners?.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={industry || '__all__'}
              onValueChange={(v) => {
                setIndustry(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]" aria-label="Filter by industry">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Industries</SelectItem>
                {filters?.industries?.map((i) => (
                  <SelectItem key={i} value={i}>
                    {i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={marketing || '__all__'}
              onValueChange={(v) => {
                setMarketing(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]" aria-label="Filter by marketing">
                <SelectValue placeholder="Marketing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Marketing</SelectItem>
                {filters?.marketingOptions?.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Open Deals</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-36" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : organizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No organizations found
                  </TableCell>
                </TableRow>
              ) : (
                organizations.map((org: PipedriveOrganization) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <Link
                        to={`/pipedrive/organizations/${org.id}`}
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        {org.name}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    </TableCell>
                    <TableCell>{org.owner || '-'}</TableCell>
                    <TableCell>{org.industry || '-'}</TableCell>
                    <TableCell>{org.email || '-'}</TableCell>
                    <TableCell>{org.phone || '-'}</TableCell>
                    <TableCell className="text-right">{org.open_deals_count ?? 0}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
